<template>
  <FriedModalSubContainer class="create-document-modal-container">
    <template #header>
      <slot name="header">
        <FriedModalHeader
          :header="header"
          :description="t('cr_create_document_modal_description')"
        />
      </slot>
    </template>
    <slot name="errorMessages" />
    <form
      id="create-document-modal-form"
      class="document-settings-form"
      @submit.prevent="$emit('create')"
    >
      <FriedInput
        :model-value="document.name"
        autofocus
        :label="t('document-name')"
        class="template-name-input full-width"
        data-external="create-document-document-name-input"
        @update:model-value="$emit('update', { name: $event.trim() })"
      />
      <FriedInput
        :model-value="document.companyName"
        :label="t('company-name')"
        class="template-name-input"
        data-external="create-document-document-name-input"
        @update:model-value="$emit('update', { companyName: $event })"
      />
      <FriedSelect
        v-if="!features[Feature.NonSignableDocuments].restricted"
        :model-value="document.type"
        :options="documentTypes"
        option-label="label"
        option-key="type"
        :label="t('document-type')"
        :placeholder="t('Select type')"
        class="template-select"
        @update:model-value="handleSelectDocumentType"
      />
      <FriedInput
        v-if="document.type === EnumDocumentType.Sales"
        :model-value="document.value"
        class="value-input input"
        type="number"
        :label="t('value')"
        :min="0"
        :max="2000000000"
        :suffix="currency.key"
        data-external="create-document-value-input"
        @update:model-value="$emit('update', { value: !!$event ? +$event : undefined })"
      />
      <FriedMessage
        v-if="showIncompatibleWarning"
        type="warning"
        :title="t('create_document_incompatible_type_warning_title')"
        class="full-width"
      >
        {{
          isNonSignable
            ? t('cr_a_presentation_cannot_be_signed_nor_approved')
            : t('cr_template_signing_description')
        }}
      </FriedMessage>
      <template v-if="!isNonSignable">
        <FriedDateTimeInput
          :model-value="document.expirationDate"
          class="input"
          data-external="assembler-header-expiration-date-input"
          :label="t('expiration-date')"
          :time-label="t('expiration-time')"
          :disabled-dates="disabledDates"
          enable-time
          @update:model-value="updateExpirationDate"
        />
        <div
          v-if="!features[Feature.NonSignableDocuments].restricted"
          class="full-width signing-toggles"
        >
          <FriedH5>{{ t('settings_signing_settings') }}</FriedH5>
          <FriedSwitch
            :model-value="!!document.isSigning"
            data-external="document-side-recipients-signing-toggle"
            :disabled="hasSignatureFields || !!qes"
            :label="t('Enable signing of document')"
            @update:model-value="$emit('update', { isSigning: +$event })"
          />
          <div class="toggle-container">
            <FriedSwitch
              v-show="document.isSigning"
              :model-value="!!document.isSelfsign"
              :disabled="senderHasSigningField || !!qes"
              data-external="document-side-recipients-self-signing-toggle"
              :label="t('Sign this document before sending')"
              @update:model-value="$emit('update', { isSelfsign: +$event })"
            />
            <FriedInfoPopper v-if="qes" trigger-data-external="document-edit-qes-info-button">
              {{ t('document_qes_disabled_self_sign_tooltip') }}
            </FriedInfoPopper>
          </div>
          <template v-if="showQes">
            <div class="toggle-container">
              <FriedSwitch
                :model-value="qes"
                data-external="document-side-qes-signing-toggle"
                :label="t('use_qes_signing_toggle')"
                :disabled="hasQesFiles"
                @update:model-value="updateQes"
              />
              <FriedInfoPopper trigger-data-external="document-edit-qes-info-button">
                <ul>
                  <li>
                    {{ $t('qes_information_modal_bullet_one') }}
                  </li>
                  <li>
                    {{ $t('qes_information_modal_bullet_two') }}
                  </li>
                  <li>
                    {{ $t('qes_information_modal_bullet_three') }}
                  </li>
                  <li>
                    {{ $t('qes_information_modal_bullet_four') }}
                  </li>
                </ul>
              </FriedInfoPopper>
            </div>
            <FriedHelpMessage
              v-if="hasQesFiles"
              class="qes-file-info-message-container"
              :message="$t('qes_files_qes_toggle_mandatory')"
            />
          </template>
        </div>
      </template>
    </form>
    <template #footer>
      <FriedModalButtonsFooter>
        <template v-if="isXSmallScreenAndUp" #left>
          <FriedButton
            data-external="create-document-upload-back-button"
            button-type="tertiary"
            :disabled="loading"
            @click="$emit('back')"
          >
            <template #leftIcon>
              <FriedIcon icon="arrow-left" />
            </template>
            {{ t('back') }}
          </FriedButton>
        </template>
        <FriedButton
          data-external="create-document-upload-cancel-button"
          button-type="secondary"
          :disabled="loading"
          @click="$emit('close')"
        >
          {{ t('cancel') }}
        </FriedButton>
        <FriedButton
          data-external="create-document-create-button"
          form="create-document-modal-form"
          button-type="primary"
          :disabled="loading || disableCreate"
        >
          {{ t('create') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModalSubContainer>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed, watch } from 'vue';
import { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import { Feature, useFeatureStore } from '@getaccept/feature-restriction';
import { DateHelper } from '@getaccept/lib-shared/src/helpers/date.helper';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { useAsyncState } from '@vueuse/core';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { FieldType } from '@getaccept/lib-shared/src/fields/types/enums/field-type';
import { FieldSource } from '@getaccept/lib-shared/src/fields/types/enums/field-source';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import { useDocumentTypes } from '../../documents/composables/document-types.composable';
import { useEntitySettings } from '../../entity/composables/entity-settings.composable';
import { useSessionStore } from '../../session/store/session.store';
import type {
  CreateDocumentPatch,
  CreateDocumentFormData,
  CreateDocumentTemplateData,
} from '../types/create-document-types';
import { DocumentService } from '../../api/documents/services/document.service';
import { AssemblerHelper } from '../../document-edit/assembler/helpers/assembler.helper';
import { CreateDocumentService } from '../services/create-document.service';

const { t } = useI18n();

const {
  document = undefined,
  hasQesFiles,
  disableCreate = false,
  loading = false,
  header = '',
  selectedTemplate = undefined,
} = defineProps<{
  document: CreateDocumentPatch;
  hasQesFiles?: boolean;
  disableCreate?: boolean;
  loading?: boolean;
  header?: string;
  selectedTemplate?: CreateDocumentTemplateData;
}>();

const emit = defineEmits<{
  update: [Partial<CreateDocumentFormData>];
  close: [];
  back: [];
  create: [];
}>();

const { features } = storeToRefs(useFeatureStore());
const { hasAccess } = useEntitySettings();
const { isXSmallScreenAndUp } = useBreakpoints();

const { currency } = storeToRefs(useSessionStore());

const isNonSignable = computed(() =>
  [EnumDocumentType.Introduction, EnumDocumentType.Presentation].includes(document?.type)
);

const hasSignatureFields = computed(() => {
  if (document.pages?.some(page => !!page.editorBlock)) {
    return AssemblerHelper.hasEditorSignatureField({ type: document.type, pages: document.pages });
  }
  return document.pages?.some(page =>
    page.fields?.some(field => [FieldType.Signature, FieldType.Initials].includes(field.type))
  );
});

const senderHasSigningField = computed(() =>
  document.pages?.some(page =>
    page.fields?.some(
      field =>
        [FieldType.Signature, FieldType.Initials].includes(field.type) &&
        field.source === FieldSource.Sender
    )
  )
);

const { documentTypes } = useDocumentTypes(hasSignatureFields.value);

const showIncompatibleWarning = computed(() => {
  if (!selectedTemplate || !selectedTemplate.isSigning || !!document.isSigning) return false;
  return (
    isNonSignable.value ||
    !!document.isSigningOrder ||
    document.recipients.some(({ role }) => role !== RecipientRole.CC)
  );
});
const disabledDates = computed(() => DateHelper.disableDateTo(DateTime.now()));
const updateExpirationDate = (value: Date) => {
  emit('update', { expirationDate: value.valueOf() });
};

const showQes = computed(
  () => hasAccess(Feature.QES) && !isNonSignable.value && !!document?.isSigning
);

const { execute: updateQesMutation } = useAsyncState(
  (value: boolean) => DocumentService.updateDocumentQES(document.id, value),
  null,
  { immediate: false }
);
const { execute: updateDocument } = useAsyncState(
  (payload: Partial<CreateDocumentPatch>) =>
    CreateDocumentService.updateDocument(document.id, payload),
  null,
  { immediate: false }
);

const { state: qes, execute: loadQes } = useAsyncState(
  () => DocumentService.getDocumentQES(document.id),
  null,
  { immediate: false }
);

const updateQes = async (value: boolean) => {
  if (value) {
    emit('update', { isSelfsign: 0 });
    await updateDocument(0, { isSelfsign: 0 });
  }
  await updateQesMutation(0, value);
  loadQes();
};
watch(
  () => hasQesFiles,
  () => {
    if (hasQesFiles && !qes.value) {
      loadQes();
      emit('update', { isSelfsign: 0 });
    }
  },
  { immediate: true }
);

const handleSelectDocumentType = (type: EnumDocumentType) => {
  let payload: Partial<CreateDocumentFormData>;
  switch (type) {
    case EnumDocumentType.Other:
      payload = { type, isSigning: 0, isSelfsign: 0, value: 0 };
      break;
    case EnumDocumentType.Sales:
    case EnumDocumentType.Hr:
      payload = document.isSigning ? { type } : { type, isSigning: 1, isSelfsign: 1 };
      break;
    case EnumDocumentType.Presentation:
    case EnumDocumentType.Introduction: {
      if (qes.value) {
        updateQes(false);
      }
      payload = { type, isSigning: 0, isSelfsign: 0, value: 0 };
      break;
    }
    default:
      payload = { type };
      break;
  }
  emit('update', payload);
};
</script>

<style lang="scss" scoped>
@import '../../css/document-settings-form';
</style>
